import { authHeaders, resolveApiBase } from './apiClient';

function parseFilenameFromContentDisposition(header) {
  if (!header) {
    return null;
  }

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = header.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim();
  }

  return null;
}

function countCsvDataRows(csvText) {
  const lines = String(csvText ?? '').trim().split(/\r?\n/).filter((line) => line.length > 0);
  return Math.max(0, lines.length - 1);
}

function buildExportFileNameFallback(portfolioName, queryParams) {
  const template = queryParams?.template ?? 'export';
  const startDate = queryParams?.startDate ?? '';
  const endDate = queryParams?.endDate ?? '';
  const slug = (value) =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  const parts = [slug(portfolioName), slug(template), startDate, endDate].filter(Boolean);
  return `${parts.join('-') || 'export'}.csv`;
}

export function saveBlobAsFile(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function fetchExportTemplates(portfolioName) {
  const path = `/api/reports/portfolio/${encodeURIComponent(portfolioName)}/export-templates`;
  const res = await fetch(`${resolveApiBase()}${path}`, {
    mode: 'cors',
    headers: await authHeaders(),
  });
  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || 'Failed to load export templates');
  }

  return data;
}

export async function downloadReportExport(portfolioName, queryParams) {
  const search = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value == null || value === '') {
      return;
    }
    if (Array.isArray(value) || typeof value === 'object') {
      search.set(key, JSON.stringify(value));
      return;
    }
    search.set(key, String(value));
  });

  const query = search.toString();
  const path = `/api/reports/portfolio/${encodeURIComponent(portfolioName)}/reports/export${
    query ? `?${query}` : ''
  }`;

  const res = await fetch(`${resolveApiBase()}${path}`, {
    mode: 'cors',
    headers: await authHeaders(),
  });
  const contentType = res.headers.get('content-type') || '';

  if (!res.ok) {
    if (contentType.includes('application/json')) {
      const data = await res.json();
      throw new Error(data?.error || 'Export request failed');
    }
    throw new Error((await res.text()) || 'Export request failed');
  }

  if (!contentType.includes('text/csv')) {
    throw new Error('Export did not return a CSV file');
  }

  const csvText = await res.text();
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const headerValue = res.headers.get('x-export-row-count');
  const parsedHeaderCount = headerValue != null && headerValue !== ''
    ? Number(headerValue)
    : NaN;
  const rowCount = Number.isFinite(parsedHeaderCount)
    ? parsedHeaderCount
    : countCsvDataRows(csvText);
  const fileName = parseFilenameFromContentDisposition(
    res.headers.get('content-disposition'),
  ) || buildExportFileNameFallback(portfolioName, queryParams);

  return { blob, fileName, rowCount };
}
